import { useState, useMemo, useEffect, useCallback, useRef, type ComponentType } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Mail,
  MailOpen,
  Trash2,
  CheckCheck,
  Send,
  Bell,
  MessageSquare,
  AlertTriangle,
  X,
  CheckSquare,
  Square,
  Minus,
  Reply,
  MessageSquareWarning,
  Maximize2,
  MoreVertical,
  Search,
  Loader2,
  Star,
  StarOff,
  Forward,
  RotateCcw,
  Link2,
  Inbox,
  SendHorizonal,
  Trash,
  MessageCircle,
  Paperclip,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useMessages, type UserMessage, type PastaCorreio } from "@/shared/hooks/useMessages";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/shared/hooks/useAuth";
import { cn } from "@/shared/lib/utils";
import { supabase } from "@/shared/integrations/supabase/client";
import ComposeMessageDialog from "./ComposeMessageDialog";
import ThreadView from "./ThreadView";
import ResumoIAModal from "./ResumoIAModal";
import {
  fetchLabelProcessoVinculo,
  hrefProcessoVinculo,
  labelTipoVinculo,
  corBadgeVinculo,
} from "./correioVinculoHelpers";

const typeIcons: Record<string, ComponentType<{ className?: string }>> = {
  direct: MessageSquare,
  system: Bell,
  feedback_response: AlertTriangle,
  feedback_recebido: MessageSquareWarning,
};

/** Remove tags HTML e decodifica entidades básicas — protege contra mensagens automáticas com HTML bruto (ex: DJE). */
function limparHtml(texto: string | null | undefined): string {
  if (!texto) return "";
  return texto
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

const typeLabels: Record<string, string> = {
  direct: "Mensagem",
  system: "Sistema",
  feedback_response: "Resposta ao seu feedback",
  feedback_recebido: "Feedback recebido",
};

const pastasConfig: { id: PastaCorreio; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: "entrada", label: "Entrada", icon: Inbox },
  { id: "enviadas", label: "Enviados", icon: SendHorizonal },
  { id: "favoritas", label: "Favoritos", icon: Star },
  { id: "lixeira", label: "Lixeira", icon: Trash },
  { id: "sistema", label: "Sistema", icon: Bell },
  { id: "feedbacks", label: "Feedbacks", icon: MessageCircle },
];

type FiltroTipo = "todos" | "nao_lidas" | "com_anexo" | "favoritas" | "com_vinculo";

const pastasPrincipais: PastaCorreio[] = ["entrada", "enviadas", "favoritas", "lixeira"];

function labelVinculo(msg: UserMessage): string | null {
  if (!msg.vinculo_id && !msg.vinculo_tipo) return null;
  if (msg.vinculo_tipo && msg.vinculo_id) return `${msg.vinculo_tipo} · ${msg.vinculo_id}`;
  return msg.vinculo_tipo || msg.vinculo_id || null;
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const avatarPalette = [
  "bg-[#791614]", "bg-[#1a5276]", "bg-[#196f3d]", "bg-[#7d3c98]",
  "bg-[#b9770e]", "bg-[#2e86c1]", "bg-[#a93226]", "bg-[#117a65]",
];

function avatarColorFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return avatarPalette[Math.abs(h) % avatarPalette.length];
}

/** Remove barra final para bater com rotas do React Router (URLs com / no fim). */
function normalizeCorreioPath(pathname: string) {
  if (!pathname) return "/";
  const p = pathname.replace(/\/+$/, "") || "/";
  return p;
}

function isCorreioRotaPagina(pathname: string) {
  const p = normalizeCorreioPath(pathname);
  return p === "/dashboard/correio" || p === "/correio-standalone";
}

function isCorreioStandalonePath(pathname: string) {
  return normalizeCorreioPath(pathname) === "/correio-standalone";
}

interface MessageInboxProps {
  /** Quando true: renderiza como página fullscreen (sem Dialog/Popover wrappers).
   *  Usado em /dashboard/correio e /correio-standalone. No layout, sem props. */
  modoPagina?: boolean;
}

const MessageInbox = ({ modoPagina = false }: MessageInboxProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const {
    messages,
    unreadCount,
    loading,
    pastaAtiva,
    hasGlowingIcon,
    setPastaAtiva,
    markAsRead,
    markAllAsRead,
    toggleFavorita,
    softDelete,
    softDeleteBatch,
    restaurar,
    hardDelete,
    deleteAllMessages,
    refetch,
  } = useMessages();

  // ── Favicon e título dinâmico (modo página apenas) ──────────────────────
  const originalTitle = useRef(document.title);
  const originalFavicon = useRef<string>("");

  const [open, setOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDialogTitle, setComposeDialogTitle] = useState("Nova Mensagem");
  const [composeDefaults, setComposeDefaults] = useState<{
    defaultRecipients?: string[];
    defaultSubject?: string;
    defaultMessage?: string;
  }>();
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** Só a instância `modoPagina` (/dashboard/correio) fica ampliada. A do header não abre Dialog
   *  na mesma rota — senão dois correios competem e o modal bloqueia portais (Select, Popover). */
  const [correioAmpliado, setCorreioAmpliado] = useState(
    () =>
      typeof window !== "undefined" &&
      modoPagina &&
      isCorreioRotaPagina(normalizeCorreioPath(window.location.pathname)),
  );
  const [mensagemDetalheId, setMensagemDetalheId] = useState<string | null>(null);

  /** Na rota dedicada o painel deve estar sempre ativo (evita página em branco se o estado atrasar). */
  useEffect(() => {
    if (modoPagina) setCorreioAmpliado(true);
  }, [modoPagina]);

  const correioPainelAtivo = modoPagina || correioAmpliado;

  useEffect(() => {
    if (!correioPainelAtivo) return;
    if (!modoPagina && !isCorreioRotaPagina(location.pathname)) return;
    if (mensagemDetalheId) return;
    const first = messages[0]?.id;
    if (first) setMensagemDetalheId(first);
  }, [correioPainelAtivo, modoPagina, location.pathname, mensagemDetalheId, messages]);
  const [buscaCorreio, setBuscaCorreio] = useState("");
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>("todos");
  const [alertApagarTudoOpen, setAlertApagarTudoOpen] = useState(false);
  const [folderCounts, setFolderCounts] = useState<Partial<Record<PastaCorreio, number>>>({});
  const [threadViewOpen, setThreadViewOpen] = useState(false);
  const [threadViewId, setThreadViewId] = useState<string | null>(null);
  const [vinculoLabelResolvido, setVinculoLabelResolvido] = useState<string | null>(null);

  const [resumoModal, setResumoModal] = useState<{
    open: boolean;
    resumoId: string;
    titulo: string;
    resumo: string;
    decisoes: string | null;
    proximosPassos: string | null;
    modelo: string;
    vinculoTipo: string;
    vinculoId: string;
    threadId: string;
    threadAssunto: string;
  } | null>(null);

  const refreshFolderCounts = useCallback(async () => {
    if (!user?.id) return;
    const uid = user.id;
    const [eUnread, fav, lix, sis, fb] = await Promise.all([
      supabase
        .from("user_messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", uid)
        .eq("read", false)
        .is("deleted_at", null)
        .then((r) => r.count ?? 0)
        .catch(() => 0),
      supabase
        .from("user_messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", uid)
        .eq("favorita", true)
        .eq("read", false)
        .is("deleted_at", null)
        .then((r) => r.count ?? 0)
        .catch(() => 0),
      supabase
        .from("user_messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", uid)
        .not("deleted_at", "is", null)
        .then((r) => r.count ?? 0)
        .catch(() => 0),
      supabase
        .from("user_messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", uid)
        .eq("message_type", "system")
        .eq("read", false)
        .is("deleted_at", null)
        .then((r) => r.count ?? 0)
        .catch(() => 0),
      supabase
        .from("user_messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", uid)
        .in("message_type", ["feedback_response", "feedback_recebido"])
        .eq("read", false)
        .is("deleted_at", null)
        .then((r) => r.count ?? 0)
        .catch(() => 0),
    ]);
    setFolderCounts({
      entrada: eUnread || undefined,
      favoritas: fav || undefined,
      lixeira: lix || undefined,
      sistema: sis || undefined,
      feedbacks: fb || undefined,
    });
  }, [user?.id]);

  const temIntimacaoNaoLida = useMemo(
    () =>
      messages.some(
        (m) => m.message_type === "system" && !m.read && m.vinculo_tipo === "judicial"
      ),
    [messages]
  );

  useEffect(() => {
    if (!correioPainelAtivo) return;
    void refreshFolderCounts();
  }, [correioPainelAtivo, refreshFolderCounts, messages.length, pastaAtiva]);

  const filtradas = useMemo(() => {
    const q = buscaCorreio.trim().toLowerCase();
    let list = messages;
    if (filtroTipo === "nao_lidas") list = list.filter((m) => !m.read);
    else if (filtroTipo === "com_vinculo") list = list.filter((m) => Boolean(m.vinculo_id || m.vinculo_tipo));
    else if (filtroTipo === "com_anexo") list = list.filter((m) => (m.attachment_count ?? 0) > 0);
    else if (filtroTipo === "favoritas") list = list.filter((m) => m.favorita);
    if (!q) return list;
    return list.filter(
      (m) =>
        m.subject.toLowerCase().includes(q) ||
        m.message.toLowerCase().includes(q) ||
        (m.sender_name || "").toLowerCase().includes(q)
    );
  }, [messages, buscaCorreio, filtroTipo]);

  useEffect(() => {
    if (!correioPainelAtivo) return;
    if (filtradas.length === 0) {
      setMensagemDetalheId(null);
      return;
    }
    if (!mensagemDetalheId || !filtradas.some((m) => m.id === mensagemDetalheId)) {
      setMensagemDetalheId(filtradas[0].id);
    }
  }, [correioPainelAtivo, filtradas, mensagemDetalheId]);

  const mensagemDetalhe = useMemo(
    () => messages.find((m) => m.id === mensagemDetalheId) ?? null,
    [messages, mensagemDetalheId]
  );

  /** Ao abrir o painel de leitura, marca como lida e reduz o contador do ícone no header. */
  useEffect(() => {
    if (!mensagemDetalhe || mensagemDetalhe.read) return;
    void markAsRead(mensagemDetalhe.id);
  }, [mensagemDetalhe?.id, mensagemDetalhe?.read, markAsRead]);

  useEffect(() => {
    if (!mensagemDetalhe?.vinculo_tipo || !mensagemDetalhe?.vinculo_id) {
      setVinculoLabelResolvido(null);
      return;
    }
    let cancelled = false;
    void fetchLabelProcessoVinculo(mensagemDetalhe.vinculo_tipo, mensagemDetalhe.vinculo_id).then((lbl) => {
      if (!cancelled) setVinculoLabelResolvido(lbl);
    });
    return () => {
      cancelled = true;
    };
  }, [mensagemDetalhe?.id, mensagemDetalhe?.vinculo_tipo, mensagemDetalhe?.vinculo_id]);

  useEffect(() => {
    if (!isCorreioRotaPagina(location.pathname)) return;
    setCorreioAmpliado(modoPagina);
  }, [modoPagina, location.pathname]);

  // ── Favicon e título dinâmico — só ativo quando modoPagina=true ──────────
  useEffect(() => {
    if (!modoPagina) return;
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (link) originalFavicon.current = link.href;
    return () => {
      if (link && originalFavicon.current) link.href = originalFavicon.current;
      document.title = originalTitle.current;
    };
  }, [modoPagina]);

  useEffect(() => {
    if (!modoPagina) return;
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    const svg = `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
        <rect width="32" height="32" rx="6" fill="#791614"/>
        <rect x="4" y="9" width="24" height="16" rx="2" fill="none" stroke="#d7b369" stroke-width="2"/>
        <polyline points="4,9 16,19 28,9" fill="none" stroke="#d7b369" stroke-width="2"/>
        ${unreadCount > 0
          ? `<circle cx="24" cy="8" r="7" fill="#b91c1c" stroke="#791614" stroke-width="1.5"/>
             <text x="24" y="12" text-anchor="middle" font-size="8" font-weight="bold" fill="white" font-family="sans-serif">${unreadCount > 9 ? "9+" : unreadCount}</text>`
          : ""}
      </svg>`,
    )}`;
    if (link) link.href = svg;
    document.title = unreadCount > 0
      ? `(${unreadCount}) Correio — Ferrugini`
      : "Correio — Ferrugini";

    let interval: ReturnType<typeof setInterval> | null = null;
    let toggle = true;
    const onHide = () => {
      if (!unreadCount) return;
      interval = setInterval(() => {
        document.title = toggle
          ? `(${unreadCount}) Correio — Ferrugini`
          : "● Correio — Ferrugini";
        toggle = !toggle;
      }, 1500);
    };
    const onShow = () => {
      if (interval) { clearInterval(interval); interval = null; }
      document.title = unreadCount > 0
        ? `(${unreadCount}) Correio — Ferrugini`
        : "Correio — Ferrugini";
    };
    const onVis = () => { if (document.hidden) onHide(); else onShow(); };
    document.addEventListener("visibilitychange", onVis);
    if (document.hidden && unreadCount > 0) onHide();
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (interval) clearInterval(interval);
    };
  }, [modoPagina, unreadCount]);

  const abrirCorreioAmpliado = () => {
    setOpen(false);
    navigate("/dashboard/correio");
  };

  const abrirNovaMensagem = useCallback(() => {
    setComposeDialogTitle("Nova Mensagem");
    setComposeDefaults(undefined);
    setComposeOpen(true);
    setOpen(false);
    if (!modoPagina) setCorreioAmpliado(false);
  }, [modoPagina]);

  const abrirResponder = useCallback(
    (msg: UserMessage) => {
      if (!msg.sender_id) {
        toast({
          title: "Não é possível responder",
          description: "Mensagens automáticas do sistema não permitem resposta por aqui.",
          variant: "destructive",
        });
        return;
      }
      if (msg.message_type === "feedback_recebido") {
        toast({
          title: "Responda no painel",
          description: "Use o painel de Feedbacks para responder ao usuário e alterar o status.",
        });
        navigate("/dashboard/administrativa/admin/feedback");
        setOpen(false);
        if (!modoPagina) setCorreioAmpliado(false);
        return;
      }
      const assunto = msg.subject.trim().toLowerCase().startsWith("re:")
        ? msg.subject
        : `Re: ${msg.subject}`;
      const citacao = `\n\n--- Mensagem original (${msg.sender_name}) ---\n${msg.message}`;
      setComposeDialogTitle("Responder");
      setComposeDefaults({
        defaultRecipients: [msg.sender_id],
        defaultSubject: assunto,
        defaultMessage: citacao,
      });
      setComposeOpen(true);
      setOpen(false);
      if (!modoPagina) setCorreioAmpliado(false);
    },
    [navigate, toast, modoPagina]
  );

  const abrirEncaminhar = useCallback((msg: UserMessage) => {
    setComposeDialogTitle("Encaminhar");
    setComposeDefaults({
      defaultSubject: msg.subject.startsWith("Enc:") ? msg.subject : `Enc: ${msg.subject}`,
      defaultMessage: `\n\n--- Mensagem encaminhada (${msg.sender_name}) ---\n${msg.message}`,
    });
    setComposeOpen(true);
    setOpen(false);
    if (!modoPagina) setCorreioAmpliado(false);
  }, [modoPagina]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtradas.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtradas.map((m) => m.id)));
    }
  };

  const handleDeleteSelected = async () => {
    await softDeleteBatch(Array.from(selectedIds));
    setSelectedIds(new Set());
    if (filtradas.length <= selectedIds.size) setSelectMode(false);
    setMensagemDetalheId((id) => (id && selectedIds.has(id) ? null : id));
    void refreshFolderCounts();
  };

  const confirmarApagarTudo = async () => {
    await deleteAllMessages();
    setSelectMode(false);
    setSelectedIds(new Set());
    setAlertApagarTudoOpen(false);
    setMensagemDetalheId(null);
    void refreshFolderCounts();
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const excluirPorId = async (id: string) => {
    await softDelete(id);
    setMensagemDetalheId((cur) => (cur === id ? null : cur));
    void refreshFolderCounts();
  };

  const excluirUma = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    void excluirPorId(id);
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return "Agora";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  const destinatarioLabel = (msg: UserMessage) => {
    if (user?.id && msg.recipient_id === user.id) return "Você";
    return "Destinatário";
  };

  const acoesMensagem = (msg: UserMessage) => (
    <Popover>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-70 hover:opacity-100" title="Mais opções">
          <MoreVertical className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1 bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]">
        {!msg.read && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start h-8 text-xs gap-2"
            onClick={() => void markAsRead(msg.id)}
          >
            <CheckCheck className="h-3.5 w-3.5" /> Marcar como lida
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start h-8 text-xs gap-2"
          onClick={() => void toggleFavorita(msg.id)}
        >
          {msg.favorita ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
          {msg.favorita ? "Remover favorita" : "Favoritar"}
        </Button>
        {msg.message_type === "feedback_recebido" && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start h-8 text-xs gap-2"
            onClick={() => {
              navigate("/dashboard/administrativa/admin/feedback");
              setOpen(false);
              if (!modoPagina) setCorreioAmpliado(false);
            }}
          >
            Painel de feedbacks
          </Button>
        )}
        {msg.sender_id && msg.message_type !== "feedback_recebido" && (
          <Button variant="ghost" size="sm" className="w-full justify-start h-8 text-xs gap-2" onClick={() => abrirResponder(msg)}>
            <Reply className="h-3.5 w-3.5" /> Responder
          </Button>
        )}
        {msg.sender_id && (
          <Button variant="ghost" size="sm" className="w-full justify-start h-8 text-xs gap-2" onClick={() => abrirEncaminhar(msg)}>
            <Forward className="h-3.5 w-3.5" /> Encaminhar
          </Button>
        )}
        <div className="h-px bg-border/40 my-1" />
        {pastaAtiva === "lixeira" ? (
          <>
            <Button variant="ghost" size="sm" className="w-full justify-start h-8 text-xs gap-2" onClick={() => void restaurar(msg.id)}>
              <RotateCcw className="h-3.5 w-3.5" /> Restaurar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start h-8 text-xs gap-2 text-destructive"
              onClick={() => void hardDelete(msg.id)}
            >
              <Trash2 className="h-3.5 w-3.5" /> Excluir permanentemente
            </Button>
          </>
        ) : (
          <Button variant="ghost" size="sm" className="w-full justify-start h-8 text-xs gap-2 text-destructive" onClick={() => void softDelete(msg.id)}>
            <Trash2 className="h-3.5 w-3.5" /> Mover para lixeira
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );

  useEffect(() => {
    if (!correioPainelAtivo) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("input, textarea, [contenteditable=true], select")) return;
      if (e.key === "Escape") {
        e.preventDefault();
        if (modoPagina && isCorreioRotaPagina(location.pathname)) {
          exitSelectMode();
          setBuscaCorreio("");
          setFiltroTipo("todos");
          if (isCorreioStandalonePath(location.pathname)) window.close();
          else navigate("/dashboard");
          return;
        }
        if (!modoPagina) setCorreioAmpliado(false);
        return;
      }
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        abrirNovaMensagem();
        return;
      }
      if (!mensagemDetalhe) return;
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        abrirResponder(mensagemDetalhe);
        return;
      }
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        abrirEncaminhar(mensagemDetalhe);
        return;
      }
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        void toggleFavorita(mensagemDetalhe.id);
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        if (pastaAtiva === "lixeira") void hardDelete(mensagemDetalhe.id);
        else void softDelete(mensagemDetalhe.id);
        setMensagemDetalheId(null);
        void refreshFolderCounts();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    correioPainelAtivo,
    mensagemDetalhe,
    abrirNovaMensagem,
    abrirResponder,
    abrirEncaminhar,
    pastaAtiva,
    softDelete,
    hardDelete,
    toggleFavorita,
    refreshFolderCounts,
    modoPagina,
    location.pathname,
    navigate,
  ]);

  const barraAcoesTopo = (opts: { compact?: boolean }) => (
    <div
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]/95 min-w-0",
        opts.compact ? "px-3 sm:px-4 py-3" : "px-4 py-3"
      )}
    >
      <div className="flex items-center gap-2 min-w-0 shrink-0">
        <Mail className="h-4 w-4 text-[#791614] shrink-0" />
        <span className="text-sm font-semibold truncate font-display text-foreground">Caixa de correio</span>
        {unreadCount > 0 && (
          <Badge variant="destructive" className="text-[10px] h-4 px-1.5 shrink-0 font-accent">
            {unreadCount}
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-1 justify-end min-w-0">
        {selectMode ? (
          <>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={toggleSelectAll}>
              {selectedIds.size === filtradas.length ? <Minus className="h-3 w-3" /> : <CheckSquare className="h-3 w-3" />}
              {selectedIds.size === filtradas.length ? "Nenhum" : "Todos"}
            </Button>
            {selectedIds.size > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-destructive" onClick={() => void handleDeleteSelected()}>
                <Trash2 className="h-3 w-3" /> Lixeira ({selectedIds.size})
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={exitSelectMode}>
              <X className="h-3 w-3" />
            </Button>
          </>
        ) : (
          <>
            {!opts.compact && !modoPagina && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 border-[#d7b369]/40 bg-[#f9f6f0]"
                onClick={() => {
                  if (!modoPagina) setCorreioAmpliado(false);
                  setOpen(true);
                }}
              >
                <Mail className="h-3 w-3" /> Pré-visualização
              </Button>
            )}
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-destructive" onClick={() => setSelectMode(true)}>
                <CheckSquare className="h-3 w-3" /> Selecionar
              </Button>
            )}
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-destructive/80" onClick={() => setAlertApagarTudoOpen(true)}>
                Esvaziar pasta
              </Button>
            )}
            {unreadCount > 0 && pastaAtiva === "entrada" && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => void markAllAsRead()}>
                <CheckCheck className="h-3 w-3" /> Marcar lidas
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-[#791614]" onClick={abrirNovaMensagem}>
              <Send className="h-3 w-3" /> Nova mensagem
            </Button>
          </>
        )}
      </div>
    </div>
  );

  const toggleFavLinha = (e: React.MouseEvent, msg: UserMessage) => {
    e.stopPropagation();
    e.preventDefault();
    void toggleFavorita(msg.id);
  };

  const renderLinhaPopover = (msg: UserMessage) => {
    const isSelected = selectedIds.has(msg.id);
    const senderName = msg.sender_name || "Sistema";
    return (
      <div
        key={msg.id}
        className={cn(
          "group flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 border-b border-border/40 last:border-b-0",
          !msg.read ? "bg-[#791614]/[0.04] border-l-[3px] border-l-[#791614]" : "border-l-[3px] border-l-transparent",
          isSelected && "bg-destructive/10"
        )}
        onClick={() => {
          if (selectMode) {
            toggleSelect(msg.id);
            return;
          }
          if (!msg.read) void markAsRead(msg.id);
          setMensagemDetalheId(msg.id);
          setOpen(false);
          navigate("/dashboard/correio");
        }}
      >
        {selectMode && (
          <div className="shrink-0 mt-1.5">
            {isSelected ? <CheckSquare className="h-4 w-4 text-[#791614]" /> : <Square className="h-4 w-4 text-muted-foreground" />}
          </div>
        )}
        <div className={cn("h-8 w-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0", avatarColorFor(senderName))}>
          {getInitials(senderName)}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <span className={cn("text-sm truncate flex-1", !msg.read ? "font-semibold text-foreground" : "text-muted-foreground")}>{senderName}</span>
            <div className="flex items-center gap-0.5 shrink-0">
              {!selectMode && (
                <>
                  {pastaAtiva !== "lixeira" ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Mover para lixeira"
                      onClick={(e) => excluirUma(e, msg.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Restaurar"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        void restaurar(msg.id);
                      }}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {acoesMensagem(msg)}
                </>
              )}
              <span className="text-[10px] text-muted-foreground tabular-nums pt-0.5 pl-1">{formatDate(msg.created_at)}</span>
            </div>
          </div>
          <p className={cn("text-xs line-clamp-2 break-words mt-0.5", !msg.read ? "font-medium text-foreground" : "text-muted-foreground/90")}>{msg.subject}</p>
          <p className="text-xs text-muted-foreground/60 line-clamp-2 break-words mt-0.5">{limparHtml(msg.message)}</p>
        </div>
        {!selectMode && (
          <button
            type="button"
            className={cn("shrink-0 mt-1 transition-opacity", msg.favorita ? "opacity-100" : "opacity-0 group-hover:opacity-100")}
            title={msg.favorita ? "Remover favorita" : "Favoritar"}
            onClick={(e) => toggleFavLinha(e, msg)}
          >
            {msg.favorita ? <Star className="h-3.5 w-3.5 fill-[#d7b369] text-[#d7b369]" /> : <Star className="h-3.5 w-3.5 text-[#a9abae]" />}
          </button>
        )}
      </div>
    );
  };

  const renderSidebarPastas = () => {
    const activeUnread = messages.filter((m) => !m.read).length;
    return (
      <div className="flex flex-col gap-0.5 w-full sm:w-[170px] shrink-0 border-b sm:border-b-0 sm:border-r border-border/40 bg-[#f9f6f0]/80 p-2">
        {pastasConfig.map((p, idx) => {
          const Icon = p.icon;
          const isPrincipal = pastasPrincipais.includes(p.id);
          const prevPrincipal = idx > 0 && pastasPrincipais.includes(pastasConfig[idx - 1].id);
          const showSep = !isPrincipal && prevPrincipal;
          const active = pastaAtiva === p.id;
          const rawCount =
            p.id === "enviadas"
              ? undefined
              : folderCounts[p.id] ?? (active ? (p.id === "lixeira" ? messages.length : activeUnread) : undefined);
          const count = rawCount && rawCount > 0 ? rawCount : undefined;
          const isUnreadType = p.id !== "lixeira" && p.id !== "enviadas";
          return (
            <div key={p.id}>
              {showSep && <div className="h-px bg-[#d7b369]/30 my-2 mx-1" />}
              <button
                type="button"
                onClick={() => {
                  setPastaAtiva(p.id);
                  setBuscaCorreio("");
                  setFiltroTipo("todos");
                  setMensagemDetalheId(null);
                }}
                className={cn(
                  "w-full flex items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors",
                  active
                    ? "bg-[#791614]/8 border-l-[3px] border-l-[#d7b369] text-[#791614] font-semibold pl-[5px]"
                    : "border-l-[3px] border-l-transparent text-muted-foreground hover:bg-muted/40"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className={cn("flex-1 truncate font-accent uppercase tracking-wide", count && isUnreadType && "font-bold")}>{p.label}</span>
                {count !== undefined && (
                  <span
                    className={cn(
                      "text-[11px] font-bold tabular-nums shrink-0 min-w-[18px] text-right",
                      isUnreadType ? "text-[#791614]" : "text-muted-foreground font-normal"
                    )}
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderListaCentral = () => (
    <div className="w-full sm:w-[320px] flex flex-col border-r border-border/40 bg-[#f9f6f0]/50 min-h-0 shrink-0">
      <div className="p-2 border-b border-border/40 space-y-2 bg-[#f9f6f0]/90">
        <Select value={filtroTipo} onValueChange={(v) => setFiltroTipo(v as FiltroTipo)}>
          <SelectTrigger className="h-8 text-xs bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]">
            <SelectValue placeholder="Filtro" />
          </SelectTrigger>
          <SelectContent className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]">
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="nao_lidas">Não lidas</SelectItem>
            <SelectItem value="com_vinculo">Com vínculo a processo</SelectItem>
            <SelectItem value="com_anexo">Com anexo</SelectItem>
            <SelectItem value="favoritas">Favoritas</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar assunto, texto ou remetente…"
            value={buscaCorreio}
            onChange={(e) => setBuscaCorreio(e.target.value)}
            className="h-8 pl-8 text-sm bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]"
          />
        </div>
      </div>
      <ScrollArea className="flex-1 min-h-0">
        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#791614]" />
          </div>
        ) : filtradas.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {messages.length === 0 ? "Caixa vazia nesta pasta." : "Nenhum resultado para os filtros."}
          </div>
        ) : (
          <div>
            {filtradas.map((msg) => {
              const ativo = mensagemDetalheId === msg.id;
              const vinculo = labelVinculo(msg);
              const senderName = msg.sender_name || "Sistema";
              return (
                <div
                  key={msg.id}
                  className={cn(
                    "group flex gap-2.5 px-3 py-2.5 cursor-pointer transition-colors hover:bg-muted/50 min-w-0 items-start border-b border-border/30",
                    ativo
                      ? "bg-[#791614]/8 border-l-[3px] border-l-[#d7b369]"
                      : !msg.read
                        ? "border-l-[3px] border-l-[#791614] bg-[#791614]/[0.03]"
                        : "border-l-[3px] border-l-transparent"
                  )}
                  onClick={() => {
                    setMensagemDetalheId(msg.id);
                    if (!msg.read) void markAsRead(msg.id);
                  }}
                >
                  <div
                    className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0 mt-0.5",
                      avatarColorFor(senderName)
                    )}
                  >
                    {getInitials(senderName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 min-w-0">
                      <span className={cn("text-[13px] truncate", !msg.read ? "font-semibold text-foreground" : "text-muted-foreground")}>
                        {senderName}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {vinculo &&
                          (() => {
                            const href =
                              msg.vinculo_tipo && msg.vinculo_id
                                ? hrefProcessoVinculo(msg.vinculo_tipo, msg.vinculo_id)
                                : null;
                            const cor = corBadgeVinculo(msg.vinculo_tipo);
                            return (
                              <span
                                title={vinculo}
                                onClick={href ? (e) => { e.stopPropagation(); navigate(href); } : undefined}
                                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-accent border
                  ${cor.bg} ${cor.text} ${cor.border}
                  ${href ? "cursor-pointer hover:opacity-75" : ""}`}
                              >
                                <Link2 className="h-2.5 w-2.5 shrink-0" />
                                {labelTipoVinculo(msg.vinculo_tipo)}
                              </span>
                            );
                          })()}
                        {(msg.attachment_count ?? 0) > 0 && <Paperclip className="h-3 w-3 text-[#a9abae]" />}
                        <span className="text-[10px] text-muted-foreground tabular-nums">{formatDate(msg.created_at)}</span>
                      </div>
                    </div>
                    <p className={cn("text-xs line-clamp-2 break-words mt-0.5", !msg.read ? "font-medium text-foreground" : "text-muted-foreground/90")}>
                      {msg.subject}
                    </p>
                    <p className="text-[11px] text-muted-foreground/60 line-clamp-2 break-words mt-0.5">{limparHtml(msg.message)}</p>
                  </div>
                  <button
                    type="button"
                    className={cn(
                      "shrink-0 mt-1 transition-opacity",
                      msg.favorita ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    )}
                    title={msg.favorita ? "Remover favorita" : "Favoritar"}
                    onClick={(e) => toggleFavLinha(e, msg)}
                  >
                    {msg.favorita ? (
                      <Star className="h-3.5 w-3.5 fill-[#d7b369] text-[#d7b369]" />
                    ) : (
                      <Star className="h-3.5 w-3.5 text-[#a9abae]" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );

  const renderPainelDetalhe = () => {
    if (!mensagemDetalhe) {
      return (
        <div className="flex-1 flex items-center justify-center p-8 text-center text-muted-foreground text-sm bg-[#f9f6f0]/40">
          Selecione uma mensagem na lista para ler o conteúdo completo.
        </div>
      );
    }
    const msg = mensagemDetalhe;
    const vinculo = vinculoLabelResolvido || labelVinculo(msg);
    return (
      <ScrollArea className="flex-1 min-h-0 bg-[#f9f6f0]/30">
        <div className="p-5 space-y-4 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h3 className="text-[17px] font-display font-semibold text-foreground leading-snug break-words">{msg.subject}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                De <span className="text-foreground font-medium">{msg.sender_name || "Sistema"}</span>
                {" · "}
                <span className="tabular-nums">{new Date(msg.created_at).toLocaleString("pt-BR")}</span>
                {" · Para "}
                <span className="text-foreground font-medium">{destinatarioLabel(msg)}</span>
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">{acoesMensagem(msg)}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs font-accent w-fit border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]">
              {typeLabels[msg.message_type] || msg.message_type}
            </Badge>
            {vinculo &&
              msg.vinculo_tipo &&
              msg.vinculo_id &&
              (() => {
                const href = hrefProcessoVinculo(msg.vinculo_tipo, msg.vinculo_id);
                const cor = corBadgeVinculo(msg.vinculo_tipo);
                return href ? (
                  <button
                    type="button"
                    onClick={() => {
                      navigate(href);
                      if (!modoPagina) setCorreioAmpliado(false);
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-accent
                  border transition-colors hover:opacity-75 cursor-pointer
                  ${cor.bg} ${cor.text} ${cor.border}`}
                  >
                    <Link2 className="h-3 w-3" />
                    {vinculo}
                  </button>
                ) : (
                  <Badge variant="outline" className={`text-xs gap-1 ${cor.border} bg-[#f9f6f0] ${cor.text}`}>
                    <Link2 className="h-3 w-3" />
                    {vinculo}
                  </Badge>
                );
              })()}
          </div>
          <div className="rounded-xl border border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] p-4 text-sm whitespace-pre-wrap break-words shadow-sm leading-relaxed">
            {limparHtml(msg.message)}
          </div>
          <div className="rounded-lg border border-dashed border-[rgba(169,171,174,0.45)] bg-[#f9f6f0]/80 p-3">
            <p className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Anexos</p>
            <p className="text-xs text-muted-foreground">Nenhum anexo listado (gestão de anexos na próxima etapa).</p>
          </div>
          {msg.read_at && (
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-1">
              <CheckCheck className="h-3 w-3 text-emerald-600" />
              Lida em {new Date(msg.read_at).toLocaleString("pt-BR")}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            {msg.message_type === "feedback_recebido" && (
              <Button
                variant="bordeaux"
                size="sm"
                onClick={() => {
                  navigate("/dashboard/administrativa/admin/feedback");
                  if (!modoPagina) setCorreioAmpliado(false);
                }}
              >
                Ir ao painel de feedbacks
              </Button>
            )}
            {msg.sender_id && msg.message_type !== "feedback_recebido" && (
              <Button variant="secondary" size="sm" className="gap-1 bg-[#f9f6f0]" onClick={() => abrirResponder(msg)}>
                <Reply className="h-3.5 w-3.5" /> Responder
              </Button>
            )}
            {msg.sender_id && (
              <Button variant="secondary" size="sm" className="gap-1 bg-[#f9f6f0]" onClick={() => abrirEncaminhar(msg)}>
                <Forward className="h-3.5 w-3.5" /> Encaminhar
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1 border-[#d7b369]/40 bg-[#f9f6f0]" onClick={() => void toggleFavorita(msg.id)}>
              {msg.favorita ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
              {msg.favorita ? "Remover favorita" : "Favoritar"}
            </Button>
            {pastaAtiva === "lixeira" ? (
              <>
                <Button variant="outline" size="sm" className="gap-1 bg-[#f9f6f0]" onClick={() => void restaurar(msg.id)}>
                  <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                </Button>
                <Button variant="outline" size="sm" className="gap-1 text-destructive border-destructive/30 bg-[#f9f6f0]" onClick={() => void hardDelete(msg.id)}>
                  <Trash2 className="h-3.5 w-3.5" /> Excluir permanentemente
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" className="gap-1 text-destructive border-destructive/30 bg-[#f9f6f0]" onClick={() => void softDelete(msg.id)}>
                <Trash2 className="h-3.5 w-3.5" /> Mover para lixeira
              </Button>
            )}
            {msg.thread_id && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1 text-xs border-[#d7b369]/40 bg-[#f9f6f0]"
                onClick={() => {
                  setThreadViewId(msg.thread_id!);
                  setThreadViewOpen(true);
                }}
              >
                <MessageSquare className="h-3.5 w-3.5" /> Ver thread completa
              </Button>
            )}
          </div>
        </div>
      </ScrollArea>
    );
  };

  const renderCorreioAmpliadoInterior = (showBordeauxHeaderBar: boolean) => (
    <>
      {/* DialogTitle/DialogDescription exigem <Dialog> — só no modal; na página dedicada usa HTML sem Radix */}
      {showBordeauxHeaderBar ? (
        <>
          <DialogHeader className="sr-only">
            <DialogTitle className="font-display">Caixa de correio</DialogTitle>
            <DialogDescription>Mensagens internas e notificações do sistema</DialogDescription>
          </DialogHeader>
          <div className="bg-[#791614] text-[#f9f6f0] px-4 py-3 flex items-center justify-between shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="h-5 w-5 shrink-0 text-[#d7b369]" />
              <span className="font-display text-lg font-semibold truncate">Caixa de correio</span>
              {unreadCount > 0 && (
                <Badge className="bg-[#d7b369] text-[#791614] border-0 text-[10px] h-5 font-accent">{unreadCount}</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && pastaAtiva === "entrada" && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-[#f9f6f0] hover:bg-[#f9f6f0]/10 hover:text-[#f9f6f0]" onClick={() => void markAllAsRead()}>
                  <CheckCheck className="h-3.5 w-3.5 mr-1" /> Lidas
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-8 text-xs text-[#f9f6f0] hover:bg-[#f9f6f0]/10 hover:text-[#f9f6f0]" onClick={abrirNovaMensagem}>
                <Send className="h-3.5 w-3.5 mr-1" /> Nova
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="sr-only">
          <h2 className="font-display">Caixa de correio</h2>
          <p>Mensagens internas e notificações do sistema</p>
        </div>
      )}
      {barraAcoesTopo({ compact: false })}
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        <div className="flex flex-1 min-h-0 flex-col sm:flex-row overflow-hidden border-t border-border/40">
          {renderSidebarPastas()}
          {renderListaCentral()}
          <div className="hidden sm:flex flex-1 flex-col min-w-0 min-h-[200px]">{renderPainelDetalhe()}</div>
        </div>
        <div className="sm:hidden flex-1 min-h-[180px] max-h-[40vh] border-t border-border/40 overflow-auto">{renderPainelDetalhe()}</div>
      </div>
      <div className="border-t border-border/40 px-3 py-2 bg-[#f9f6f0]/95 text-[10px] text-muted-foreground font-accent flex items-center gap-2 justify-between">
        <span>
          Atalhos: <kbd className="px-1 rounded border border-[#a9abae]/50 bg-[#f9f6f0]">N</kbd> nova · <kbd className="px-1 rounded border border-[#a9abae]/50 bg-[#f9f6f0]">R</kbd>{" "}
          responder · <kbd className="px-1 rounded border border-[#a9abae]/50 bg-[#f9f6f0]">F</kbd> encaminhar · <kbd className="px-1 rounded border border-[#a9abae]/50 bg-[#f9f6f0]">S</kbd>{" "}
          favoritar · <kbd className="px-1 rounded border border-[#a9abae]/50 bg-[#f9f6f0]">Del</kbd> lixeira
        </span>
      </div>
    </>
  );

  return (
    <>
      {!modoPagina && (
        <Popover
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) exitSelectMode();
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" title="Mensagens">
              <Mail
                className={cn(
                  "h-4 w-4 transition-all",
                  temIntimacaoNaoLida
                    ? "text-[#791614] animate-pulse drop-shadow-[0_0_8px_rgba(121,22,20,0.6)]"
                    : hasGlowingIcon
                      ? "text-[#d7b369] animate-pulse drop-shadow-[0_0_6px_rgba(215,179,105,0.7)]"
                      : ""
                )}
              />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-[min(400px,calc(100vw-24px))] p-0 overflow-hidden border-[rgba(169,171,174,0.35)] shadow-lg bg-[#f9f6f0]"
            sideOffset={8}
          >
            {barraAcoesTopo({ compact: true })}
            <div className="flex items-center justify-between px-3 py-2 border-b border-border/40 bg-[#f9f6f0]/80">
              <span className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">Pré-visualização</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 text-[#791614]"
                  onClick={abrirCorreioAmpliado}
                  title="Abrir correio na mesma aba"
                >
                  <Maximize2 className="h-3 w-3" /> Ampliar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 text-[#a9abae] hover:text-[#791614]"
                  title="Abrir em janela independente"
                  onClick={() => {
                    setOpen(false);
                    window.open(
                      "/correio-standalone",
                      "correio-ferrugini",
                      "width=1100,height=700,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no",
                    );
                  }}
                >
                  ⧉
                </Button>
              </div>
            </div>
            <ScrollArea className="max-h-[min(400px,55vh)]">
              {loading ? (
                <div className="py-12 flex flex-col items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin text-[#791614]" />
                  <span className="text-sm">Carregando mensagens…</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="py-10 text-center px-4">
                  <MailOpen className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Nenhuma mensagem na caixa</p>
                  <Button variant="link" className="text-[#791614] text-sm mt-1 h-auto p-0" onClick={abrirNovaMensagem}>
                    Escrever nova mensagem
                  </Button>
                </div>
              ) : (
                <div>{messages.map((msg) => renderLinhaPopover(msg))}</div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      )}

      {correioPainelAtivo &&
        (modoPagina ? (
          <div className="flex flex-col h-full bg-[#f9f6f0] overflow-hidden">
            <div className="shrink-0 h-12 flex items-center justify-between px-4 border-b border-[#d7b369]/30 bg-[#791614]">
              <div className="flex items-center gap-3">
                <span className="font-display text-[#d7b369] text-lg font-semibold tracking-wide">Correio Interno</span>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#b91c1c] text-white text-[10px] font-bold font-accent">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[#d7b369] hover:text-white hover:bg-white/10 font-accent text-[10px] uppercase tracking-widest gap-1.5"
                onClick={() => {
                  exitSelectMode();
                  setBuscaCorreio("");
                  setFiltroTipo("todos");
                  if (isCorreioStandalonePath(location.pathname)) window.close();
                  else navigate("/dashboard");
                }}
              >
                {isCorreioStandalonePath(location.pathname) ? "Fechar janela" : "← Dashboard"}
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">{renderCorreioAmpliadoInterior(false)}</div>
          </div>
        ) : (
          <Dialog
            open={correioAmpliado}
            onOpenChange={(v) => {
              setCorreioAmpliado(v);
              if (!v) {
                exitSelectMode();
                setBuscaCorreio("");
                setFiltroTipo("todos");
                if (isCorreioRotaPagina(location.pathname)) {
                  if (isCorreioStandalonePath(location.pathname)) window.close();
                  else navigate("/dashboard");
                }
              }
            }}
          >
            <DialogContent
              className={cn(
                "flex flex-col gap-0 p-0 w-[calc(100%-1rem)] max-w-[min(96vw,1120px)] h-[min(88dvh,820px)] max-h-[90dvh] overflow-hidden",
                "border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] rounded-2xl"
              )}
            >
              {renderCorreioAmpliadoInterior(true)}
            </DialogContent>
          </Dialog>
        ))}

      <AlertDialog open={alertApagarTudoOpen} onOpenChange={setAlertApagarTudoOpen}>
        <AlertDialogContent className="border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Esvaziar pasta atual?</AlertDialogTitle>
            <AlertDialogDescription>
              As {messages.length} mensagens visíveis nesta pasta serão movidas para a lixeira (exclusão reversível). Respostas de feedback vinculadas podem ser arquivadas no painel
              administrativo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#f9f6f0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => void confirmarApagarTudo()}>
              Mover tudo para lixeira
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ComposeMessageDialog
        open={composeOpen}
        onOpenChange={(v) => {
          setComposeOpen(v);
          if (!v) {
            setComposeDefaults(undefined);
            setComposeDialogTitle("Nova Mensagem");
          }
        }}
        dialogTitle={composeDialogTitle}
        defaultRecipients={composeDefaults?.defaultRecipients}
        defaultSubject={composeDefaults?.defaultSubject}
        defaultMessage={composeDefaults?.defaultMessage}
      />

      {threadViewId && (
        <ThreadView
          open={threadViewOpen}
          onOpenChange={(v) => {
            setThreadViewOpen(v);
            if (!v) setThreadViewId(null);
          }}
          threadId={threadViewId}
          onConcluir={() => void refetch()}
          onResumoGerado={(p) => {
            setResumoModal({
              open: true,
              resumoId: p.resumoId,
              titulo: p.titulo,
              resumo: p.resumo,
              decisoes: p.decisoes,
              proximosPassos: p.proximosPassos,
              modelo: p.modelo,
              vinculoTipo: p.vinculoTipo,
              vinculoId: p.vinculoId,
              threadId: p.threadId,
              threadAssunto: p.threadAssunto,
            });
          }}
        />
      )}

      {resumoModal?.open && (
        <ResumoIAModal
          open={resumoModal.open}
          onOpenChange={(open) => {
            if (!open) {
              setResumoModal(null);
              void refetch();
            }
          }}
          resumoId={resumoModal.resumoId}
          titulo={resumoModal.titulo}
          resumo={resumoModal.resumo}
          decisoes={resumoModal.decisoes}
          proximosPassos={resumoModal.proximosPassos}
          modelo={resumoModal.modelo}
          vinculoTipo={resumoModal.vinculoTipo}
          vinculoId={resumoModal.vinculoId}
          threadId={resumoModal.threadId}
          threadAssunto={resumoModal.threadAssunto}
        />
      )}
    </>
  );
};

export default MessageInbox;
